import { useEffect, useState } from 'react';

import type { TicketDetail } from '@nightshift/ui-types';

import { Markdown } from '../../components/markdown.tsx';
import { api } from '../../live/api.ts';
import {
  OBSOLETE_KIND_LABEL,
  currentObsoleteCheckArtifact,
  currentObsoleteClaimArtifact,
  parseObsoleteCheckText,
  parseObsoleteClaimText,
  type ObsoleteCheckView,
  type ObsoleteClaimView,
} from '../../lib/obsolete-claim.ts';

/** An evidence line's `code` spans, which agents write as Markdown, without a block renderer. */
function InlineCode({ text }: { text: string }) {
  const parts = text.split('`');
  // An odd count of backticks leaves the last span unclosed: show the text as written.
  if (parts.length % 2 === 0) return <>{text}</>;
  return <>{parts.map((part, index) => (index % 2 === 1 ? <code key={index}>{part}</code> : part))}</>;
}

/**
 * The refiner says this ticket is no longer needed. Show its evidence, then let a person agree
 * (cancel, which Restore undoes) or keep it with a note the next refinement reads instead of
 * proposing the same cancellation again.
 */
export function ObsoleteProposalPanel({
  detail,
  busy,
  onCancelTicket,
  onKeep,
  onClose,
}: {
  detail: TicketDetail;
  busy: boolean;
  onCancelTicket: (reason: string) => void;
  onKeep: (note: string) => void;
  onClose: () => void;
}) {
  const artifact = currentObsoleteClaimArtifact(detail);
  const artifactId = artifact?.id ?? null;
  const [claim, setClaim] = useState<ObsoleteClaimView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkId = currentObsoleteCheckArtifact(detail)?.id ?? null;
  const [check, setCheck] = useState<ObsoleteCheckView | null>(null);
  const [keeping, setKeeping] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    let active = true;
    setClaim(null);
    setError(null);
    if (artifactId === null) return () => {
      active = false;
    };
    void api
      .artifactText(artifactId)
      .then((body) => {
        if (!active) return;
        const parsed = parseObsoleteClaimText(body.text);
        if (parsed) setClaim(parsed);
        else setError('The stored claim could not be read. The refiner\'s comment has the same evidence.');
      })
      .catch((caught) => {
        if (active) setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      active = false;
    };
  }, [artifactId]);

  useEffect(() => {
    let active = true;
    setCheck(null);
    if (checkId === null) return () => {
      active = false;
    };
    // The check is supporting detail: if it cannot be read the claim and the buttons still work.
    void api
      .artifactText(checkId)
      .then((body) => {
        if (active) setCheck(parseObsoleteCheckText(body.text));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [checkId]);

  const reason = claim
    ? `Obsolete (${OBSOLETE_KIND_LABEL[claim.kind].toLowerCase()}): ${claim.reason.split('\n')[0] ?? claim.reason}`.slice(0, 500)
    : `Obsolete: ${detail.waiting?.reason ?? 'proposed by the refiner'}`.slice(0, 500);

  return (
    <section className="rise drawer-obsolete" aria-label="Proposed cancellation">
      <div className="drawer-obsolete-head">
        <strong>Proposed cancellation</strong>
        {claim ? (
          <span className="drawer-obsolete-chips">
            <span className="chip">{OBSOLETE_KIND_LABEL[claim.kind]}</span>
            <span className="chip">Confidence: {claim.confidence}</span>
            {claim.harness ? <span className="chip">Refiner: {claim.harness}</span> : null}
          </span>
        ) : null}
      </div>
      {claim ? (
        <>
          <div className="drawer-obsolete-reason">
            <Markdown source={claim.reason} tight />
          </div>
          <ul className="drawer-obsolete-evidence" aria-label="Evidence">
            {claim.evidence.map((entry, index) => (
              <li key={`${entry.path}:${entry.line ?? ''}:${index}`}>
                <span className="mono">{entry.line !== undefined ? `${entry.path}:${entry.line}` : entry.path}</span>
                <span> <InlineCode text={entry.observation} /></span>
              </li>
            ))}
          </ul>
          {claim.resolvedBy ? (
            <p className="drawer-obsolete-meta">
              Made unnecessary by <span className="mono">{claim.resolvedBy}</span>
            </p>
          ) : null}
        </>
      ) : error ? (
        <p className="drawer-obsolete-meta">{error}</p>
      ) : artifactId === null ? (
        <p className="drawer-obsolete-meta">{detail.waiting?.reason}</p>
      ) : (
        <p className="drawer-obsolete-meta">Loading the refiner's evidence…</p>
      )}
      {check ? (
        <div className="drawer-obsolete-check" aria-label="Independent check">
          <p className="drawer-obsolete-meta">
            Independent check{check.harness ? ` (${check.harness})` : ''}:{' '}
            {check.verdict === 'obsolete-confirmed' ? 'confirmed the claim' : 'did not confirm the claim'}
          </p>
          <div className="drawer-obsolete-reason">
            <Markdown source={check.reason} tight />
          </div>
          {check.evidence.length > 0 ? (
            <ul className="drawer-obsolete-evidence" aria-label="Check evidence">
              {check.evidence.map((entry, index) => (
                <li key={`${entry.path}:${entry.line ?? ''}:${index}`}>
                  <span className="mono">{entry.line !== undefined ? `${entry.path}:${entry.line}` : entry.path}</span>
                  <span> <InlineCode text={entry.observation} /></span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {keeping ? (
        <div className="drawer-obsolete-keep">
          <textarea
            className="input"
            rows={3}
            autoFocus
            value={note}
            placeholder="Why is this ticket still needed? The next refinement reads this."
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="drawer-obsolete-actions">
            <button
              type="button"
              className="btn btn-accent btn-sm"
              disabled={busy || note.trim().length === 0}
              onClick={() => onKeep(note.trim())}
            >
              Keep and refine again
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setKeeping(false)}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="drawer-obsolete-actions">
          <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => onCancelTicket(reason)}>
            Cancel as obsolete
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setKeeping(true)}>
            Keep it
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      )}
      <p className="drawer-obsolete-meta">
        Cancelling closes any merge request and keeps the branch. Restore brings the ticket back to Backlog.
      </p>
    </section>
  );
}
